// Shared, dependency-free document rules for the browser and both relays.
export const MAX_NOTES_BYTES = 700 * 1024;
export const MAX_NOTES_RECORDS = 1000;
export const NOTES_ID_RE = /^[a-zA-Z0-9_-]{8,100}$/;
const FIELD_RE = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;
const object = (value) => !!value && typeof value === "object" && !Array.isArray(value);
const safeKey = (key) => !["__proto__", "constructor", "prototype"].includes(key);
const scalar = (value) => value === null || typeof value === "boolean" ||
  (typeof value === "string" && value.length <= 12000) ||
  (typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= 1e16);
function validFields(fields) {
  return object(fields) && Object.keys(fields).length <= 64 && Object.entries(fields).every(([key, register]) =>
    safeKey(key) && FIELD_RE.test(key) && object(register) &&
    Object.keys(register).length === 3 && scalar(register.value) &&
    Number.isSafeInteger(register.clock) && register.clock >= 0 && register.clock < 1e12 &&
    typeof register.actor === "string" && NOTES_ID_RE.test(register.actor));
}
export function validProductionDocument(doc) {
  if (!object(doc) || Object.keys(doc).some((key) => !["version", "projectId", "fields", "records"].includes(key)) ||
    doc.version !== 1 || typeof doc.projectId !== "string" || !NOTES_ID_RE.test(doc.projectId) ||
    !validFields(doc.fields) || !object(doc.records) || Object.keys(doc.records).length > MAX_NOTES_RECORDS ||
    !Object.entries(doc.records).every(([id, record]) => safeKey(id) && NOTES_ID_RE.test(id) && object(record) &&
      Object.keys(record).length === 1 && validFields(record.fields))) return false;
  try { return new TextEncoder().encode(JSON.stringify(doc)).length <= MAX_NOTES_BYTES; } catch { return false; }
}
function newer(a, b) {
  if (!a) return b;
  if (!b) return a;
  if (a.clock !== b.clock) return a.clock > b.clock ? a : b;
  if (a.actor !== b.actor) return a.actor > b.actor ? a : b;
  // Even an imported document that equivocates at one stamp converges.
  return JSON.stringify(a.value) >= JSON.stringify(b.value) ? a : b;
}
function mergeFields(a, b) {
  return Object.fromEntries([...new Set([...Object.keys(a), ...Object.keys(b)])].sort()
    .map((key) => [key, newer(a[key], b[key])]));
}
export function mergeProductionDocuments(a, b) {
  if (!validProductionDocument(a) || !validProductionDocument(b) || a.projectId !== b.projectId)
    throw new Error("Production notes belong to a different project or contain invalid data.");
  const doc = { version: 1, projectId: a.projectId, fields: mergeFields(a.fields, b.fields), records:
    Object.fromEntries([...new Set([...Object.keys(a.records), ...Object.keys(b.records)])].sort()
      .map((id) => [id, { fields: mergeFields(a.records[id]?.fields || {}, b.records[id]?.fields || {}) }])) };
  if (!validProductionDocument(doc)) throw new Error("Production notes have reached their storage limit. Export this project before starting another.");
  return doc;
}
export function productionDocumentClock(doc) {
  let clock = 0;
  for (const register of Object.values(doc.fields)) clock = Math.max(clock, register.clock);
  for (const record of Object.values(doc.records)) for (const register of Object.values(record.fields)) clock = Math.max(clock, register.clock);
  return clock;
}
